import { motion } from "framer-motion";
import { ArrowRight, TrendingUp } from "lucide-react";
import { SAVINGS_SCENARIOS } from "@/lib/site";

const fmt = (n) => "$" + n.toLocaleString("es-CL");

function annualSavings(s) {
  return (s.market - s.clamignon) * s.volumeNum * 12;
}
function monthlySavings(s) {
  return (s.market - s.clamignon) * s.volumeNum;
}

export default function SavingsCalculator() {
  return (
    <section id="ahorro" data-testid="savings-section" className="relative py-20 md:py-28">
      <div className="max-w-[1160px] mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="chip mb-5">
            <span className="dot" /> Ahorro real · CLP sin IVA
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-[56px] font-bold text-[#0B0B14] tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Cuánto ahorra tu clínica <span className="text-[#5A48C9]">al año.</span>
          </h2>
          <p className="mt-5 text-[#3A3A48] max-w-xl mx-auto">
            Calculado sobre el precio de referencia de mercado B2B Chile 2025
            ($42.900 / unidad · marca coreana media). Números por escrito.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {SAVINGS_SCENARIOS.map((s, i) => {
            const monthly = monthlySavings(s);
            const annual = annualSavings(s);
            return (
              <motion.div
                key={s.volume}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                data-testid={`savings-card-${i}`}
                className={`relative p-7 md:p-8 rounded-[28px] flex flex-col ${
                  s.highlight ? "card-glass" : "card-soft"
                }`}
              >
                {s.highlight && (
                  <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[#0B0B14] text-white text-[10px] font-bold uppercase tracking-widest">
                    El más común
                  </span>
                )}
                <div className="flex items-center justify-between mb-6">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5A48C9]">
                    {s.label}
                  </span>
                  <TrendingUp className="w-4 h-4 text-[#5A48C9]" strokeWidth={2} />
                </div>

                <p className="font-display text-3xl md:text-4xl font-bold text-[#0B0B14] tracking-tight leading-none">
                  {s.volume}
                </p>

                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex items-center justify-between text-[#6B6B7D]">
                    <span>Mercado</span>
                    <span className="font-semibold text-[#3A3A48]">{fmt(s.market)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#5A48C9] font-semibold">Clamignon</span>
                    <span className="font-bold text-[#0B0B14]">{fmt(s.clamignon)}</span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-black/[0.08]">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#6B6B7D] mb-1">
                    Ahorro / mes
                  </p>
                  <p className="font-display text-2xl font-bold text-[#0B0B14] tracking-tight">
                    {fmt(monthly)}
                  </p>
                </div>

                <div className="mt-4 rounded-2xl bg-gradient-to-b from-[#8B7CFF] to-[#5A48C9] p-5 text-white">
                  <p className="text-[10px] font-semibold uppercase tracking-widest opacity-80">
                    Ahorro / año
                  </p>
                  <p className="mt-1 font-display text-3xl md:text-4xl font-bold tracking-tight leading-none">
                    {fmt(annual)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 md:mt-12 rounded-[28px] card-glass p-8 md:p-10 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 md:col-span-8">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#5A48C9] mb-3">
              Traducido a tu negocio
            </p>
            <p className="font-display text-2xl md:text-3xl font-bold text-[#0B0B14] tracking-tight leading-tight max-w-2xl">
              Un semestre completo de margen recuperado — sin subir el precio a tu
              paciente ni bajar el estándar clínico.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end">
            <a
              data-testid="savings-cta"
              href="#contacto"
              className="btn-pill btn-primary shine text-[15px] px-6 py-3.5 w-full md:w-auto"
            >
              Ver mi ahorro exacto
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <p className="mt-6 text-[11px] font-semibold uppercase tracking-widest text-[#6B6B7D] text-center">
          Cálculo referencial · Diferencia unitaria × volumen mensual × 12 · CLP sin IVA
        </p>
      </div>
    </section>
  );
}
